import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang, localizePath, type Lang } from '../../lib/lang';
import programsData from '../../data/programsData.json';

// The CICC public-register profile for Yansi He (R708210) — the same canonical
// deep link the JSON-LD sameAs, the home trust strip and the chatbot KB already
// publish (scripts/routeSchemas.mjs).
const CICC_REGISTER_URL =
  'https://register.college-ic.ca/Public-Register-EN/Licensee/Profile.aspx?ID=15486';

// T188 ④ — the footer trust block's door to the methodology page (owner ruling
// B, 2026-07-26: four entries, one per moment of doubt; this one is the
// wayfinding fallback). The licence says WHO stands behind the work; this line
// says HOW the work is done — same trust cluster, so it sits with the licence.
// Copy is local + bilingual because src/i18n/** is outside this task's
// write-face (the T180 badge made the same call, hub-approved); it is locked
// verbatim, jargon-scanned and compliance-linted by Footer.methodologyEntry.test.tsx.
const METHODOLOGY_ENTRY: Record<Lang, string> = {
  en: 'How we verify your forms',
  zh: '我们如何核对表格',
};

// [T223 · U2] v2 footer — MINIMAL WHITE (styles-v2.css §18; design-hybrid §7
// bans the navy footer outright). Shape per card §2: the brand takes a full row
// of its own above a hairline, the three column headings share one baseline, and
// the legal row sits under a second hairline.
//
// The bottom padding is not decoration. A floating chat launcher parks at
// `bottom-5 right-5`; without a reserved band the last footer row — the legal
// links — sits underneath it and is unclickable. 110px is the ground truth's own
// reserve (styles-v2.css:840) and it clears I32's ≥88px floor with margin.
//
// It is UNCONDITIONAL, not mobile-only as the ground truth has it, because our
// launcher is not the ground truth's 48px circle at every width: above `sm` it
// expands into a 168×56 capsule. Measured in Chromium at 1440×900 with the band
// released above 40rem, the capsule (x 1252, y 824) sat squarely on top of the
// English "Terms of Service" button (x 1172, y 856) — overlaps: true. A reserve
// that is only correct on the viewport the launcher is smallest on is not a
// reserve. Locked by u2Assembly.test.tsx and re-measured in the browser.
//
// T95's skin pass moved this block off the retired gray-900 ground; U2 completes
// the move to white. The one surviving slate-50 surface is the social button
// tile, which is exactly what the ground truth specifies for it
// (`.social-btn { background: var(--surface) }`, and --surface IS #f8fafc).
export default function Footer() {
  const { t } = useTranslation();
  const lang = useLang();
  const lp = (p: string) => localizePath(p, lang);

  // [T235] The ground truth's social row. LinkedIn is the one REAL outbound
  // profile this repo holds, and it is the ground truth's own link.
  //
  // 小红书 / RED — DELIBERATELY ABSENT, with the slot kept. The ground truth
  // publishes a RED short link; a social short link is an unverifiable redirect
  // target, and never-invent covers "found in a reference file" exactly as it
  // covers "sounded right". T235's card rules it the same way: no real link in
  // hand ⇒ no button, keep the slot. Owner supplies the profile URL and it
  // becomes one entry in this array:
  //   TODO_OWNER_XHS_LINK — add { name: 'RED', icon: 'ri-book-2-fill', href: '<url>', ariaLabel: 'RED / Xiaohongshu' }
  // The ground truth's other two tiles (Google Business Profile, WeChat) point
  // at `href="#"` there; a tile that goes nowhere is a broken promise with an
  // icon on it, so WeChat keeps the placeholder it already had and nothing new
  // joins it. GBP lands with the P0-2 profile task, not here.
  const socialLinks = [
    {
      name: 'WeChat',
      icon: 'ri-wechat-fill',
      href: '#',
      ariaLabel: 'WeChat'
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      href: 'https://www.linkedin.com/in/yansi-he-aa107863/',
      ariaLabel: 'LinkedIn'
    }
  ];

  // [T235] Column 1 is now OUR SERVICES — the ground truth's six service links,
  // one per detail page, replacing the five Readdy-era "Expertise" anchors that
  // pointed at four hub fragments (a taxonomy the navigation stopped using when
  // the six program pages landed).
  //
  // The labels are NOT transcribed from the ground truth: they are read from
  // `programsData.json`, the file that also titles the destination page. A label
  // typed here can drift from the page it opens; a label read from the page's
  // own record cannot. Both languages come from the same rows, so the zh footer
  // cannot silently fall back to English either.
  const serviceLinks = (programsData.programs as Array<{ slug: string; name: { en: string; zh: string } }>).map(
    (p) => ({ key: p.slug, label: p.name[lang], href: `/services/${p.slug}` }),
  );

  const companyLinks = [
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.pricing'), href: '/pricing' },
    { name: t('nav.testimonials'), href: '/testimonials' },
    { name: t('nav.bookConsultation'), href: '/book-consultation' },
    { name: t('nav.faq'), href: '/faq' },
    { name: t('nav.policyUpdates'), href: '/policy-updates' }
  ];

  const columnHeading = 'mb-[0.8rem] text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-v2-muted';
  // [T235] The column links now clear the I32 44px touch floor. This is not a
  // decoration change bought with a taller footer — it is the arithmetic the
  // craft ledger forced, and the ledger was right:
  //
  // This card gives the first column a SIXTH link (the ground truth's six
  // service pages, replacing five hub anchors). Every footer link was already
  // a ~20px-tall target carried as pre-existing I32 debt, so a sixth one adds
  // one more piece of that debt to EVERY route on the site — +46 targets, and
  // `craftLedger.json` only ratchets DOWN. The choice was to grow the ledger
  // (forbidden, and rightly: it is how a ratchet stops working) or to pay the
  // floor on the surface being rebuilt. Paid: 13 links × 46 routes leave the
  // sub-44px count entirely, which is the biggest single reduction the ledger
  // has recorded.
  //
  // `flex` + `min-h-[44px]` rather than padding + a negative margin: the trick
  // version keeps the compact 27px rhythm by making each hit box overlap its
  // neighbours, and two overlapping 44px targets are worse to tap than two
  // honest 20px ones. The row gap goes to zero because the height now supplies
  // the rhythm.
  const columnLink =
    'flex min-h-[44px] items-center cursor-pointer text-[0.85rem] text-v2-muted transition-colors duration-brand ease-out hover:text-v2-ink';

  return (
    <>
      <footer
        data-v2
        className="v2-footer border-t border-v2-line bg-white pb-[110px] text-v2-muted"
      >
        <div className="mx-auto w-[min(100%-3rem,70rem)] pt-12">
          {/* ── brand: its own full row, hairline underneath (card §2) ─────── */}
          <div className="mb-8 border-b border-v2-line pb-6">
            <Link to={lp('/')} className="inline-flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-600">
                <i className="ri-compass-3-line text-2xl text-white"></i>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold leading-tight tracking-tight text-v2-ink" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Think Forward
                </span>
                <span className="text-xs font-medium uppercase leading-tight tracking-wider text-teal-700" style={{ fontFamily: '"Montserrat", sans-serif', letterSpacing: '0.1em' }}>
                  Immigration Consulting
                </span>
              </div>
            </Link>
            <p className="mt-4 max-w-[60ch] text-[0.85rem] leading-relaxed text-v2-muted">
              {t('footer.mission')}
            </p>
          </div>

          {/* ── three columns, headings on one baseline (`items-start`) ─────── */}
          <div className="grid grid-cols-1 items-start gap-8 pb-10 v2-min40:grid-cols-[1.1fr_0.9fr_1.4fr] v2-min40:gap-10">
            {/* Column 1: Our Services — one link per service page (T235) */}
            <div>
              <h3 className={columnHeading}>{t('footer.services.title')}</h3>
              <ul className="grid list-none p-0">
                {serviceLinks.map((item) => (
                  <li key={item.key}>
                    <Link to={lp(item.href)} className={columnLink}>
                      {item.label}
                    </Link>
                  </li>
                ))}
                {/* [T216] The SECOND footer Login entry used to sit here, in the
                    Expertise column, duplicating the one in COMPANY below — the
                    same destination offered twice in one footer, which reads as two
                    different doors. The COMPANY column keeps it (that is where a
                    reader looks for account/portal entries); the R6 assertion
                    (`href="/intake"` in BOTH header and footer) still holds through
                    the surviving pair. */}
              </ul>
            </div>

            {/* Column 2: Company */}
            <div>
              <h3 className={columnHeading}>{t('footer.company.title')}</h3>
              <ul className="grid list-none p-0">
                {companyLinks.map((link) => (
                  <li key={link.href}>
                    <Link to={lp(link.href)} className={columnLink}>
                      {link.name}
                    </Link>
                  </li>
                ))}
                {/* Portal entry — /intake has no /zh variant, so never lp() it (T75).
                    Re-added after the T98 train stop dropped it (footer side taken
                    from T95); locked by the R6 footer assertion. */}
                <li key="/intake">
                  <Link to="/intake" className={columnLink}>
                    {t('nav.login')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3: Regulated & Contact — the compliance column.
                The T216 authorization wording is rendered verbatim from its key;
                the licence number and the register deep link sit with it, because
                a regulatory claim a reader cannot check is not a trust signal. */}
            <div>
              <h3 className={columnHeading}>{t('footer.licensing.title')}</h3>

              <div className="grid gap-[0.6rem] text-[0.85rem] leading-relaxed text-v2-muted">
                <p>{t('footer.licensing.authorized')}</p>
                {/* The name is its own text node in its registered form
                    (`Yansi He`) — the i18n audit exempts exactly that form as a
                    proper noun identical in both languages (i18n-audit.mjs:150);
                    gluing a separator onto it makes a new, unexempt string. */}
                <p>
                  <span className="font-semibold text-v2-ink">{t('footer.licensing.license')}</span>{' '}
                  <span className="font-semibold text-teal-700">R708210</span>
                  <span className="text-v2-muted"> — </span>
                  <span className="text-v2-muted">Yansi He</span>
                </p>
                <p>
                  <a
                    href={CICC_REGISTER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-teal-700 transition-colors duration-brand ease-out hover:text-teal-800"
                  >
                    {t('home.credibility.cicc')}
                    <i className="ri-external-link-line ml-1 align-middle" aria-hidden="true"></i>
                  </a>
                </p>
              </div>

              {/* T188 ④ — methodology entry, inside the licence/trust cluster */}
              <Link
                to={lp('/methodology')}
                className="mt-4 inline-flex items-start gap-2 cursor-pointer text-[0.85rem] text-v2-muted transition-colors duration-brand ease-out hover:text-v2-ink"
              >
                <i className="ri-shield-check-line mt-0.5 flex-shrink-0 text-teal-700" aria-hidden="true"></i>
                <span>{METHODOLOGY_ENTRY[lang]}</span>
              </Link>

              {/* Contact Details */}
              <ul className="mt-4 grid list-none gap-[0.4rem] p-0 text-[0.85rem] text-v2-muted">
                <li className="flex items-start gap-2">
                  <i className="ri-mail-line mt-0.5 flex-shrink-0 text-teal-700" aria-hidden="true"></i>
                  <a
                    href="mailto:info@thinkforwardic.com"
                    className="cursor-pointer break-all transition-colors duration-brand ease-out hover:text-v2-ink"
                  >
                    info@thinkforwardic.com
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-map-pin-line mt-0.5 flex-shrink-0 text-teal-700" aria-hidden="true"></i>
                  <span>{t('footer.contact.address')}</span>
                </li>
              </ul>

              {/* Social — 32px tiles on the v2 surface (`--surface` = slate-50) */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-v2-line bg-slate-50 text-teal-700 transition-colors duration-brand ease-out hover:border-teal-600 hover:bg-teal-600 hover:text-white cursor-pointer"
                    aria-label={social.ariaLabel}
                  >
                    <i className={`${social.icon} text-xl`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* ── legal row under its own hairline (card §2「法律列 hairline」) ── */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-v2-line py-6">
            <p className="text-[0.82rem] text-v2-muted">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </p>
            {/* [T235] The legal pair are PAGES now, not modals. A privacy policy
                a reader cannot link to, bookmark, print or send to a regulator is
                not a published policy — and a modal has no URL. The ground truth
                has the same two links here.

                ⚠ MERGE ORDER: /privacy and /terms are built by T237, which the
                hub merges FIRST (both cards say so). The modal components stay in
                the repo untouched — booking and contact still open them inline at
                the consent step, which is the one place a modal is right: you are
                agreeing to the thing, so it should not navigate you away. */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link
                to={lp('/privacy')}
                className="cursor-pointer whitespace-nowrap text-[0.82rem] text-v2-muted transition-colors duration-brand ease-out hover:text-v2-ink"
              >
                {t('footer.privacy')}
              </Link>
              <Link
                to={lp('/terms')}
                className="cursor-pointer whitespace-nowrap text-[0.82rem] text-v2-muted transition-colors duration-brand ease-out hover:text-v2-ink"
              >
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
